import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApiError, api } from '../../api/client.js';
import { DataTable, ErrorState, Field, Modal, ReasonModal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { StatTile } from '../../components/charts.jsx';
import { ClaimReceipt } from '../../components/ClaimReceipt.jsx';
import { useAuth } from '../../state/AuthContext.jsx';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatDate, formatXaf } from '../../lib/format.js';
import { ACCOUNT_BADGE } from './UtilityService.jsx';
import { LATE_FEE_REASONS } from '../../components/lease.jsx';
import { MoneySummary, UtilityRefundPanel } from '../../components/refunds.jsx';

const BILL_BADGE = {
  draft: { status: 'draft', label: 'Draft' },
  due: { status: 'pending', label: 'Due' },
  partly_paid: { status: 'under_review', label: 'Part paid' },
  paid: { status: 'approved', label: 'Paid' },
  waived: { status: 'withdrawn', label: 'Waived' },
  cancelled: { status: 'withdrawn', label: 'Cancelled' }
};

/** The arithmetic behind a bill, written out the way the bill itself shows it. */
const Workings = ({ bill, unitLabel }) => {
  // The sum before rounding: the figure the standing charge and consumption
  // actually add up to.
  const exact = bill.exact ?? bill.amount;
  // Each step is only spelled out when it changed the figure, otherwise the same
  // number reads two or three times in a row.
  const tail = bill.standingCharge > 0 ? ` + ${formatXaf(bill.standingCharge)} standing = ${formatXaf(exact)}` : '';
  const rounded = bill.rounding > 0 ? `, rounded up to ${formatXaf(bill.amount)}` : '';

  // A balance carried over from before has no dial behind it, and calling it a
  // fixed charge would hide where it came from.
  if (bill.broughtForward) {
    return (
      <span className="muted">
        Carried over from before - {bill.broughtForward}
      </span>
    );
  }

  if (bill.billingMode !== 'metered') {
    return (
      <span className="muted">
        Fixed charge {formatXaf(bill.consumption)}
        {tail}
        {rounded}
      </span>
    );
  }

  return (
    <span className="muted">
      {bill.currentReading} - {bill.previousReading} = {bill.unitsUsed} {unitLabel} x {formatXaf(bill.unitCost)} ={' '}
      {formatXaf(bill.consumption)}
      {tail}
      {rounded}
    </span>
  );
};

/**
 * The customer's own details on this meter.
 *
 * The meter number is the landlord's record of which physical dial this person
 * is billed from, so it is theirs to change and nobody else's - a manager sees
 * it, and is told plainly why they cannot touch it rather than being handed a
 * box that fails on save.
 */
const AccountDetailsModal = ({ account, canEditMeter, neverBilled, onSubmit, onClose }) => {
  // Where the meter started stays the landlord's to set until it has been
  // billed from - most dials were turning long before anybody recorded them.
  // The first bill fixes it, because from then on every bill counts up from it
  // and moving it would rewrite what has already been paid.
  const openingLocked = !(canEditMeter && neverBilled);
  const [form, setForm] = useState({
    label: account.label || '',
    meterNumber: account.meterNumber || '',
    openingReading: String(account.openingReading ?? '0'),
    notes: account.notes || ''
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit({
        label: form.label.trim() || null,
        // Left out entirely when it cannot be changed, so a manager saving a
        // note never trips the guard on a field they did not touch.
        ...(canEditMeter ? { meterNumber: form.meterNumber.trim() || null } : {}),
        ...(openingLocked ? {} : { openingReading: Number(form.openingReading || 0) }),
        notes: form.notes.trim() || null
      });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`${account.customer?.name} on this meter`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="account-details-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <form id="account-details-form" className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field label="What to call it" hint="Back room, the shop, Flat 2." error={errors.label}>
            <input type="text" value={form.label} onChange={(event) => set({ label: event.target.value })} />
          </Field>

          <Field
            label="Meter number"
            hint={
              canEditMeter
                ? 'The dial this customer is billed from.'
                : 'Only the landlord can change which meter somebody is billed from.'
            }
            error={errors.meterNumber}
          >
            <input
              type="text"
              value={form.meterNumber}
              onChange={(event) => set({ meterNumber: event.target.value })}
              disabled={!canEditMeter}
            />
          </Field>
        </div>

        <div className="grid-2">
          <Field
            label="First reading"
            hint={
              openingLocked
                ? 'Fixed: this meter has been billed from here, so moving it would rewrite what was owed.'
                : 'What the dial was really on when they went on the meter. Billing counts up from here, and the first bill fixes it.'
            }
            error={errors.openingReading}
          >
            <input
              type="number"
              min="0"
              step="0.001"
              value={form.openingReading}
              onChange={(event) => set({ openingReading: event.target.value })}
              disabled={openingLocked}
            />
          </Field>

          <Field label="Notes" error={errors.notes}>
            <input type="text" value={form.notes} onChange={(event) => set({ notes: event.target.value })} />
          </Field>
        </div>
      </form>
    </Modal>
  );
};

/**
 * The month a reading taken today belongs to.
 *
 * A bill with no period belongs to no month, so it never appears beside the
 * main meter - and one dated part-way through a month lands in whichever month
 * that date falls in, which is rarely what the landlord meant. Defaulting to
 * the whole of the current month puts it where the main meter reading for the
 * same month is, which is where they will go looking for it.
 */
const thisMonth = () => {
  const now = new Date();
  const first = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
  const last = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0));
  return { start: first.toISOString().slice(0, 10), end: last.toISOString().slice(0, 10) };
};

const inDays = (count) => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + count);
  return date.toISOString().slice(0, 10);
};

/**
 * Reading the meter and billing it in one action. The running total comes from
 * the server rather than being worked out again here, so there is only one copy
 * of the arithmetic and a customer cannot be shown a figure the bill disagrees
 * with.
 */
const IssueBillModal = ({
  accountId,
  service,
  previousReading,
  // True while this meter has never been billed. Most meters were running long
  // before the landlord started recording them, so the first time one is read
  // the starting figure is theirs to type in rather than a fact to be shown.
  canSetPrevious = false,
  bill = null,
  replaces = null,
  onSubmit,
  onClose
}) => {
  // With a `bill` this is a correction of one nobody has paid; otherwise it is a
  // fresh reading. The arithmetic and the fields are the same either way, so the
  // form is too - it only gains a reason box.
  const correcting = Boolean(bill);
  const [form, setForm] = useState({
    currentReading: bill?.currentReading != null ? String(bill.currentReading) : '',
    previousReading: String(previousReading ?? 0),
    periodStart: bill?.periodStart || thisMonth().start,
    periodEnd: bill?.periodEnd || thisMonth().end,
    dueDate: bill?.dueDate || inDays(7),
    reason: ''
  });
  const [calculation, setCalculation] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const metered = service.billingMode === 'metered';

  // Debounced so a typed reading does not fire a request per keystroke.
  useEffect(() => {
    if (metered && form.currentReading === '') {
      setCalculation(null);
      return undefined;
    }

    const timer = setTimeout(() => {
      api
        .post(`/landlord/utility-accounts/${accountId}/bills/preview`, {
          ...(metered ? { currentReading: Number(form.currentReading) } : {}),
          ...(canSetPrevious ? { openingReading: Number(form.previousReading || 0) } : {}),
          ...(correcting ? { excludeBillId: bill.id } : {})
        })
        .then((result) => setCalculation(result.calculation))
        .catch(() => setCalculation(null));
    }, 250);

    return () => clearTimeout(timer);
  }, [accountId, metered, form.currentReading, form.previousReading, canSetPrevious, correcting, bill?.id]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit({
        ...(metered ? { currentReading: Number(form.currentReading) } : {}),
        ...(canSetPrevious ? { openingReading: Number(form.previousReading || 0) } : {}),
        periodStart: form.periodStart || null,
        periodEnd: form.periodEnd || null,
        dueDate: form.dueDate,
        ...(correcting ? { reason: form.reason.trim() } : {}),
        ...(replaces ? { replacesBillId: replaces } : {})
      });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  const sum = calculation
    ? metered
      ? `${calculation.unitsUsed} ${service.unitLabel} at ${formatXaf(service.unitCost)} per ${service.unitLabel}` +
        (calculation.standingChargeXaf > 0
          ? `, plus a ${formatXaf(calculation.standingChargeXaf)} standing charge`
          : '') +
        `. Total: ${formatXaf(calculation.amountXaf)}.`
      : `Fixed charge of ${formatXaf(calculation.consumptionXaf)}` +
        (calculation.standingChargeXaf > 0 ? ` plus ${formatXaf(calculation.standingChargeXaf)}` : '') +
        `. Total: ${formatXaf(calculation.amountXaf)}.`
    : null;

  return (
    <Modal
      title={correcting ? `Correct bill ${bill.reference}` : metered ? 'Read the meter and draw up the bill' : 'Draw up the bill'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="utility-bill-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : correcting ? 'Save the correction' : 'Save as a draft'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      {correcting ? null : (
        <p className="modal-lead">
          This is saved as a draft. Nothing is emailed and the customer sees nothing until you post it, so a misread
          dial can be fixed quietly first.
        </p>
      )}

      <form id="utility-bill-form" className="stack" onSubmit={submit}>
        {metered ? (
          <div className="grid-2">
            <Field
              label="Last reading"
              hint={
                canSetPrevious
                  ? 'Where the dial stood before this. Set it to what the meter was really on - most meters were in use long before you started recording them. After the first bill it is fixed.'
                  : 'What this meter was on when it was last billed.'
              }
              error={errors.openingReading}
            >
              {canSetPrevious ? (
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={form.previousReading}
                  onChange={(event) => set({ previousReading: event.target.value })}
                />
              ) : (
                <input type="text" value={previousReading} readOnly disabled />
              )}
            </Field>

            <Field label="Reading now" error={errors.currentReading} required>
              <input
                type="number"
                min="0"
                step="0.001"
                value={form.currentReading}
                onChange={(event) => set({ currentReading: event.target.value })}
                required
                autoFocus
              />
            </Field>
          </div>
        ) : null}

        {sum ? (
          <div className="notice notice-info utility-sum">
            <strong>{formatXaf(calculation.amountXaf)}</strong>
            <span>{sum}</span>
          </div>
        ) : null}

        <div className="grid-3">
          <Field
            label="Period from"
            hint="The month this reading belongs to. It is what puts the bill beside the main meter for that month."
          >
            <input type="date" value={form.periodStart} onChange={(event) => set({ periodStart: event.target.value })} />
          </Field>

          <Field label="Period to">
            <input type="date" value={form.periodEnd} onChange={(event) => set({ periodEnd: event.target.value })} />
          </Field>

          <Field
            label="Pay by"
            hint="The service is cut off two days after this if it is not paid."
            error={errors.dueDate}
            required
          >
            <input
              type="date"
              value={form.dueDate}
              onChange={(event) => set({ dueDate: event.target.value })}
              required
            />
          </Field>
        </div>

        {correcting ? (
          <Field
            label="What are you correcting, and why"
            hint="Kept against the bill, so the change can always be explained."
            error={errors.reason}
            required
          >
            <input
              type="text"
              value={form.reason}
              onChange={(event) => set({ reason: event.target.value })}
              placeholder="Dial actually read 1305"
              required
            />
          </Field>
        ) : null}
      </form>
    </Modal>
  );
};

const RecordPaymentModal = ({ bill, onSubmit, onClose }) => {
  const { paymentMethods } = useMeta();
  const [form, setForm] = useState({
    amount: String(bill.outstanding ?? bill.amount),
    method: 'cash',
    reference: '',
    note: ''
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit({
        amount: Number(form.amount),
        method: form.method,
        reference: form.reference.trim() || null,
        note: form.note.trim() || null
      });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Record a payment against ${bill.reference}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="utility-payment-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Record it'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <form id="utility-payment-form" className="stack" onSubmit={submit}>
        <Field label="Amount in FCFA" hint={`${formatXaf(bill.outstanding ?? bill.amount)} outstanding.`} error={errors.amount} required>
          <input
            type="number"
            min="1"
            step="1"
            value={form.amount}
            onChange={(event) => set({ amount: event.target.value })}
            required
          />
        </Field>

        <Field label="How it arrived" error={errors.method}>
          <select value={form.method} onChange={(event) => set({ method: event.target.value })}>
            {paymentMethods.map((method) => (
              <option key={method.slug} value={method.slug}>
                {method.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Reference" hint="A MoMo code or teller slip number, if there is one." error={errors.reference}>
          <input type="text" value={form.reference} onChange={(event) => set({ reference: event.target.value })} />
        </Field>

        <Field label="Note" error={errors.note}>
          <input type="text" value={form.note} onChange={(event) => set({ note: event.target.value })} />
        </Field>
      </form>
    </Modal>
  );
};

const UtilityAccount = () => {
  const { id } = useParams();
  const toast = useToast();
  // Removing a bill destroys the row, so it is the landlord's own decision and
  // never a manager's - the API refuses them too.
  const { isLandlord } = useAuth();
  const [dialog, setDialog] = useState(null);
  // Arriving from "Refund" on the customer list opens the refund straight away;
  // each press of "Refund it" here remounts the panel open.
  const [searchParams] = useSearchParams();
  const [refundAsk, setRefundAsk] = useState(searchParams.get('refund') === '1' ? 1 : 0);

  const account = useFetch(`/landlord/utility-accounts/${id}`, [id]);
  useTitle(account.data?.account.customer?.name || 'Utility account');

  if (account.loading && !account.data) return <Spinner />;
  if (account.error) {
    return (
      <div className="container section">
        <ErrorState message={account.error} onRetry={account.reload} />
      </div>
    );
  }

  const { account: row, service, bills, payments, totals, previousReading, mainMeter } = account.data;
  // Where this meter started is still the landlord's to set until it has been
  // billed from; a cancelled bill does not count, since it was a mistake.
  const neverBilled = bills.every((bill) => bill.status === 'cancelled');

  const issue = async (payload) => {
    await api.post(`/landlord/utility-accounts/${id}/bills`, payload);
    toast.success('Saved as a draft. Check it, then post it to send it.');
    setDialog(null);
    account.reload();
  };

  const correct = async (payload) => {
    await api.patch(`/landlord/utility-bills/${dialog.bill.id}`, payload);
    toast.success(
      dialog.bill.posted
        ? 'Corrected. The customer has been sent the corrected bill.'
        : 'Corrected. Still a draft, so nobody has been told.'
    );
    setDialog(null);
    account.reload();
  };

  const postBill = async (bill) => {
    try {
      await api.post(`/landlord/utility-bills/${bill.id}/post`);
      toast.success('Posted. The customer has been emailed the reading and the total.');
      account.reload();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const cancelBill = async (reason) => {
    await api.post(`/landlord/utility-bills/${dialog.bill.id}/cancel`, { reason });
    toast.success('Cancelled. It stays on the books with the reason against it.');
    setDialog(null);
    account.reload();
  };

  const removeBill = async (reason) => {
    await api.del(`/landlord/utility-bills/${dialog.bill.id}`, { body: { reason } });
    toast.success('Removed. What it was and why it went is on your activity feed.');
    setDialog(null);
    account.reload();
  };

  const reviewClaim = async (claim, action, reason) => {
    try {
      await api.post(`/landlord/utility-claims/${claim.id}/${action}`, reason ? { reason } : undefined);
      toast.success(action === 'confirm' ? 'Confirmed. The receipt has been emailed over.' : 'Turned down.');
      setDialog(null);
      account.reload();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const pay = async (payload) => {
    const result = await api.post(`/landlord/utility-bills/${dialog.bill.id}/payments`, payload);
    toast.success(
      result.reconnectQueued
        ? 'Recorded. The service is back on - go and reconnect it, then confirm on the worklist.'
        : 'Recorded. The receipt has been emailed over.'
    );
    setDialog(null);
    account.reload();
  };

  const attachPhoto = async (bill, file) => {
    try {
      const body = new FormData();
      body.append('photo', file);
      await api.upload(`/landlord/utility-bills/${bill.id}/photo`, body);
      toast.success(
        bill.posted
          ? 'Photograph attached. The customer sees it on their bill.'
          : 'Photograph attached to the draft.'
      );
      account.reload();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const confirmJob = async (what) => {
    await api.post(`/landlord/utility-accounts/${id}/confirm-${what}`);
    toast.success(what === 'disconnection' ? 'Marked as disconnected.' : 'Marked as back on.');
    account.reload();
  };

  // The evidence a metered reading needs before it can be sent. Mirrors
  // whyNotSendable in server/src/modules/utilities.js.
  const needsPhoto = (bill) =>
    service.billingMode === 'metered' && !bill.broughtForward && !bill.readingPhotoUrl;

  const billColumns = [
    {
      key: 'reference',
      header: 'Bill',
      render: (bill) => (
        <div className="cell-stack">
          <span className="cell-title">{bill.reference}</span>
          <span className="muted">
            {bill.periodStart ? `${formatDate(bill.periodStart)} to ${formatDate(bill.periodEnd)}` : formatDate(bill.readOn)}
          </span>
        </div>
      )
    },
    {
      key: 'reading',
      header: 'How it was worked out',
      render: (bill) => (
        <div className="cell-stack">
          <Workings bill={bill} unitLabel={service.unitLabel} />

          {/* The dial this was read off, so the customer can check the figure
              rather than take it on trust. */}
          {bill.readingPhotoUrl ? (
            <a href={bill.readingPhotoUrl} target="_blank" rel="noreferrer" className="meter-photo meter-photo-sm">
              <img src={bill.readingPhotoUrl} alt={`The meter on ${formatDate(bill.readOn)}`} />
            </a>
          ) : null}
          <label className="link-btn photo-pick">
            {bill.readingPhotoUrl ? 'Replace the photo' : 'Add a photo of the dial'}
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) attachPhoto(bill, file);
              }}
            />
          </label>
          {bill.revisions?.length ? (
            <details className="bill-history">
              <summary>
                corrected {bill.revisions.length} {bill.revisions.length === 1 ? 'time' : 'times'}
              </summary>
              <ul className="plain-list">
                {bill.revisions.map((revision) => (
                  <li key={revision.id}>
                    <strong>{formatDate(revision.createdAt)}</strong>
                    {revision.changedByName ? ` by ${revision.changedByName}` : ''} - {revision.reason}
                    <ul className="plain-list">
                      {Object.entries(revision.changes).map(([field, change]) => (
                        <li key={field} className="muted">
                          {field.replace(/_xaf$/, '').replace(/_/g, ' ')}: {String(change.from)} to{' '}
                          {String(change.to)}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {bill.cancelReason ? <span className="overdue-flag">cancelled: {bill.cancelReason}</span> : null}
        </div>
      )
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (bill) => (
        <div className="cell-stack">
          <strong>{formatXaf(bill.total)}</strong>
          {/* The penalty as its own line: the meter's figure has not changed,
              something was added to it, and both ought to be readable. */}
          {bill.lateFee > 0 ? (
            <span className="overdue-flag">
              {formatXaf(bill.amount)} + {formatXaf(bill.lateFee)} late fee
            </span>
          ) : null}
          {bill.lateFeeWaived ? <span className="muted">late fee dropped</span> : null}
          {bill.paid > 0 ? <span className="muted">{formatXaf(bill.paid)} received</span> : null}
          {bill.outstanding > 0 ? <span className="owing">{formatXaf(bill.outstanding)} outstanding</span> : null}
          {bill.claimed > 0 ? (
            <span className="claim-flag">{formatXaf(bill.claimed)} reported, not confirmed</span>
          ) : null}
        </div>
      )
    },
    {
      key: 'due',
      header: 'Deadline',
      render: (bill) => (
        <div className="cell-stack">
          <span>{bill.posted ? formatDate(bill.dueDate) : <span className="muted">not sent yet</span>}</span>
          {bill.overdue ? <span className="overdue-flag">cut off {formatDate(bill.cutOffDate)}</span> : null}
        </div>
      )
    },
    { key: 'status', header: 'Status', render: (bill) => <StatusBadge {...BILL_BADGE[bill.status]} /> },
    {
      key: 'actions',
      header: '',
      render: (bill) => (
        <div className="cell-actions">
          {/* A metered bill is a claim about a number on a dial, and the
              customer cannot check a reading they cannot see - so it does not
              go out without the photograph. A fixed charge and a balance
              carried over have no dial behind them. */}
          {!bill.posted && bill.status !== 'cancelled' ? (
            needsPhoto(bill) ? (
              <span className="muted" title="Attach a photograph of the dial first">
                Photograph the dial to send it
              </span>
            ) : (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => postBill(bill)}>
                Post to customer
              </button>
            )
          ) : null}

          {bill.posted && bill.outstanding > 0 ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setDialog({ kind: 'pay', bill })}>
              Record payment
            </button>
          ) : null}

          {bill.editable ? (
            <button type="button" className="link-btn" onClick={() => setDialog({ kind: 'correct', bill })}>
              Correct
            </button>
          ) : null}

          {/* Correcting a sent bill posts the corrected copy straight away.
              Taking it back first lets a wrong figure be put right once, quietly,
              before the customer is told anything further. */}
          {isLandlord && bill.posted && bill.editable ? (
            <button type="button" className="link-btn" onClick={() => setDialog({ kind: 'withdraw', bill })}>
              Take it back
            </button>
          ) : null}

          {/* The clock charges the fee; only the landlord can forgive it. */}
          {isLandlord && bill.lateFee > 0 ? (
            <button type="button" className="link-btn" onClick={() => setDialog({ kind: 'late-fee', bill })}>
              Drop the late fee
            </button>
          ) : null}

          {bill.status !== 'cancelled' ? (
            <button
              type="button"
              className="link-btn link-btn-danger"
              onClick={() => setDialog({ kind: 'cancel', bill })}
            >
              Cancel
            </button>
          ) : (
            <button
              type="button"
              className="link-btn"
              onClick={() => setDialog({ kind: 'bill', replaces: bill.id })}
            >
              Issue a replacement
            </button>
          )}

          {isLandlord ? (
            <button
              type="button"
              className="link-btn link-btn-danger"
              onClick={() => setDialog({ kind: 'remove', bill })}
            >
              Remove
            </button>
          ) : null}
        </div>
      )
    }
  ];

  const pendingClaims = bills.flatMap((bill) => (bill.claims || []).map((claim) => ({ ...claim, bill })));

  const paymentColumns = [
    {
      key: 'receipt',
      header: 'Receipt',
      render: (payment) => (
        <div className="cell-stack">
          <Link to={`/utility-receipts/${payment.id}`}>{payment.receiptNumber}</Link>
          {payment.voided ? <span className="void-flag">cancelled</span> : null}
        </div>
      )
    },
    { key: 'paidAt', header: 'Date', render: (payment) => formatDate(payment.paidAt) },
    { key: 'amount', header: 'Amount', render: (payment) => formatXaf(payment.amount) },
    { key: 'method', header: 'How', render: (payment) => payment.method.replace(/_/g, ' ') },
    {
      key: 'actions',
      header: '',
      render: (payment) => (
        <Link to={`/utility-receipts/${payment.id}`} className="btn btn-ghost btn-sm">
          View or print
        </Link>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>{row.customer?.name}</h1>
          <p className="muted">
            {service.name}
            {row.meterNumber ? ` - meter ${row.meterNumber}` : ''}
            {row.label ? ` - ${row.label}` : ''}
            {row.isTenant ? ' - a tenant of yours' : ' - not a tenant'}
            {' - '}
            <button type="button" className="link-btn" onClick={() => setDialog({ kind: 'details' })}>
              edit details
            </button>
          </p>
        </div>
        <div className="row-actions">
          <Link to={`/landlord/utilities/${service.id}`} className="btn btn-ghost">
            Back to the service
          </Link>
          <button type="button" className="btn btn-primary" onClick={() => setDialog({ kind: 'bill' })}>
            {service.billingMode === 'metered' ? 'Read the meter' : 'Raise a bill'}
          </button>
        </div>
      </header>

      {row.status === 'cut_off' ? (
        <div className="notice notice-bad">
          <strong>This service is cut off.</strong>{' '}
          {row.disconnectedAt
            ? 'The disconnection has been confirmed. Recording the outstanding payment will put it back on.'
            : 'It still needs cutting physically.'}
          {row.disconnectedAt ? null : (
            <button type="button" className="link-btn" onClick={() => confirmJob('disconnection')}>
              I have cut it off
            </button>
          )}
        </div>
      ) : null}

      {row.reconnectDueAt && !row.reconnectedAt ? (
        <div className="notice notice-warn">
          <strong>Waiting to be put back on.</strong> The debt is settled since {formatDate(row.reconnectDueAt)}.{' '}
          <button type="button" className="link-btn" onClick={() => confirmJob('reconnection')}>
            I have put it back on
          </button>
        </div>
      ) : null}

      {pendingClaims.length > 0 ? (
        <section className="section-block">
          <div className="section-head">
            <h2>Waiting on you</h2>
            <span className="muted">
              Money {row.customer?.name} says they have sent. None of it counts until you confirm it.
            </span>
          </div>
          <div className="claim-list">
            {pendingClaims.map((claim) => (
              <article className="claim-card" key={claim.id}>
                <div>
                  <strong>{formatXaf(claim.amount)}</strong>
                  <span className="muted">
                    {claim.method.replace(/_/g, ' ')}
                    {claim.reference ? ` - ${claim.reference}` : ''} - paid {formatDate(claim.paidOn)}
                  </span>
                  <span className="muted">
                    against {claim.bill.reference} ({formatXaf(claim.bill.outstanding)} outstanding)
                  </span>
                  {claim.note ? <span className="muted">"{claim.note}"</span> : null}
                  <ClaimReceipt url={claim.receiptUrl} />
                </div>
                <div className="cell-actions">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => reviewClaim(claim, 'confirm')}
                  >
                    Confirm and receipt it
                  </button>
                  <button
                    type="button"
                    className="link-btn link-btn-danger"
                    onClick={() => setDialog({ kind: 'reject', claim })}
                  >
                    Not received
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {mainMeter?.required && !mainMeter.recorded ? (
        <div className="notice notice-warn main-first">
          <div>
            <strong>
              The {service.name} main meter for{' '}
              {new Date(`${mainMeter.month}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })}{' '}
              is not recorded yet.
            </strong>{' '}
            Record it on the service first - the reading and what {service.name} charged. This customer can be billed for
            the month once it is.
          </div>
          <Link to={`/landlord/utilities/${service.id}`} className="btn btn-primary btn-sm">
            Record the main meter
          </Link>
        </div>
      ) : null}

      <MoneySummary
        billed={totals.billed}
        paid={totals.received}
        owed={totals.owed}
        credit={totals.credit}
        refunded={totals.refunded}
        overdue={totals.overdue}
        onRefund={isLandlord && totals.credit > 0 ? () => setRefundAsk((count) => count + 1) : null}
      />

      <div className="stat-row">
        {totals.awaitingConfirmation > 0 ? (
          <StatTile
            value={formatXaf(totals.awaitingConfirmation)}
            label="Reported, not confirmed"
            note="Counts for nothing until you agree"
            tone="warn"
          />
        ) : null}
        {totals.drafted > 0 ? (
          <StatTile value={formatXaf(totals.drafted)} label="In draft" note="Not sent to anybody yet" />
        ) : null}
        <StatTile
          value={service.billingMode === 'metered' ? `${previousReading} ${service.unitLabel}` : '-'}
          label="Meter last read at"
        />
        <StatTile value={<StatusBadge {...ACCOUNT_BADGE[row.status]} />} label="Service" />
      </div>

      <div id="refund">
        <UtilityRefundPanel key={refundAsk} accountId={row.id} openOnLoad={refundAsk > 0} onChanged={account.reload} />
      </div>

      <section className="section-block">
        <div className="section-head">
          <h2>Bills and readings</h2>
          <span className="muted">Every reading taken on this meter, and what it came to.</span>
        </div>
        <DataTable
          columns={billColumns}
          rows={bills}
          empty={{
            title: 'Nothing billed yet',
            message:
              service.billingMode === 'metered'
                ? 'Read the meter and the first bill is worked out and emailed for you.'
                : 'Raise the first bill and it is emailed over with the deadline on it.'
          }}
        />
      </section>

      <section className="section-block">
        <div className="section-head">
          <h2>Receipts</h2>
          <span className="muted">Money received on this meter.</span>
        </div>
        <DataTable
          columns={paymentColumns}
          rows={payments}
          empty={{ title: 'No receipts yet', message: 'Record a payment and the receipt appears here.' }}
        />
      </section>

      {dialog?.kind === 'bill' ? (
        <IssueBillModal
          accountId={id}
          service={service}
          previousReading={previousReading}
          canSetPrevious={isLandlord && neverBilled}
          replaces={dialog.replaces ?? null}
          onSubmit={issue}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === 'correct' ? (
        <IssueBillModal
          accountId={id}
          service={service}
          previousReading={dialog.bill.previousReading}
          bill={dialog.bill}
          onSubmit={correct}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === 'cancel' ? (
        <ReasonModal
          title={`Cancel bill ${dialog.bill.reference}`}
          lead={
            dialog.bill.paid > 0
              ? 'This bill has had money taken against it. Cancelling leaves the bill and the receipts on the books, and what was paid becomes a credit on this meter.'
              : 'The bill stays on the books, marked cancelled, with your reason against it. You can then issue a corrected one.'
          }
          label="Why is it being cancelled"
          confirmLabel="Cancel the bill"
          tone="danger"
          onSubmit={cancelBill}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === 'details' ? (
        <AccountDetailsModal
          account={row}
          canEditMeter={isLandlord}
          neverBilled={neverBilled}
          onClose={() => setDialog(null)}
          onSubmit={async (payload) => {
            await api.patch(`/landlord/utility-accounts/${id}`, payload);
            toast.success('Saved.');
            setDialog(null);
            account.reload();
          }}
        />
      ) : null}

      {dialog?.kind === 'withdraw' ? (
        <ReasonModal
          title={`Take back bill ${dialog.bill.reference}`}
          lead={
            `It goes back to being a draft: off the customer's page, no deadline running, and any late fee on it ` +
            `dropped. They are told not to pay it and that a corrected one will follow. Put it right, then send it ` +
            `again - it keeps the same number.`
          }
          label="Why is it being taken back"
          confirmLabel="Take it back"
          onSubmit={async (reason) => {
            await api.post(`/landlord/utility-bills/${dialog.bill.id}/withdraw`, { reason });
            toast.success('Taken back. It is a draft again - correct it, then send it.');
            setDialog(null);
            account.reload();
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === 'late-fee' ? (
        <ReasonModal
          title={`Drop the ${formatXaf(dialog.bill.lateFee)} late fee`}
          lead={
            `Bill ${dialog.bill.reference} was not paid by its deadline, so the fee was added automatically. ` +
            `Dropping it leaves ${formatXaf(dialog.bill.amount)} to pay, and the customer is told. ` +
            `The fee stays on the bill marked as dropped, with your reason against it.`
          }
          label="Why is it being dropped"
          confirmLabel="Drop it"
          suggestions={LATE_FEE_REASONS}
          onSubmit={async (reason) => {
            await api.post(`/landlord/utility-bills/${dialog.bill.id}/waive-late-fee`, { reason });
            toast.success('Dropped. They have been told.');
            setDialog(null);
            account.reload();
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === 'remove' ? (
        <ReasonModal
          title={`Remove bill ${dialog.bill.reference}`}
          lead={
            dialog.bill.paid > 0
              ? 'This bill has receipts against it, so it cannot be removed until those are cancelled. Cancelling the bill instead leaves everything on the books.'
              : dialog.bill.posted
                ? 'This will delete the bill completely. The customer has already been sent it, so they will be told it is withdrawn. What it was and why it went is kept on your activity feed.'
                : 'This will delete the draft completely. Nobody has seen it. What it was and why it went is kept on your activity feed.'
          }
          label="Why is it being removed"
          confirmLabel="Remove it"
          tone="danger"
          onSubmit={removeBill}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === 'reject' ? (
        <ReasonModal
          title="Turn down this payment"
          lead={`${formatXaf(dialog.claim.amount)} was reported but you have not received it. The customer is told why and the bill stays open.`}
          label="What should they be told"
          confirmLabel="Turn it down"
          tone="danger"
          onSubmit={(reason) => reviewClaim(dialog.claim, 'reject', reason)}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === 'pay' ? (
        <RecordPaymentModal bill={dialog.bill} onSubmit={pay} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
};

export default UtilityAccount;
